import { ScheduleEntryStatus } from "./DetailedItem";
import { parseAppointmentDeposits } from "../../utils/appointmentDeposits";
import { parseSegmentGapOverrides } from "./segmentGapOverrides";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const weekConfig = {
  weekDays: [0, 1, 2, 3, 4, 5, 6],
  weekStartOn: 0,
  startHour: 9,
  endHour: 20,
  step: 15,
  navigation: true,
  disableGoToDay: false,
};

export const dayConfig = {
  startHour: 9,
  endHour: 20,
  step: 15,
  navigation: true,
};

/** Bookable window shown on the scheduler (used to constrain time pickers). */
export const SCHEDULER_BOOKING_START_HOUR = weekConfig.startHour;
export const SCHEDULER_BOOKING_END_HOUR = weekConfig.endHour;

export const SCHEDULER_HOURS_ERROR =
  "This time is outside salon hours (9:00 AM – 8:00 PM). If you changed only the hour, check AM/PM.";

export function validateSchedulerBookingHours(start, end) {
  const startTime = dayjs(start);
  const endTime = dayjs(end);
  const windowStart = startTime
    .startOf("day")
    .hour(SCHEDULER_BOOKING_START_HOUR)
    .minute(0)
    .second(0);
  const windowEnd = startTime
    .startOf("day")
    .hour(SCHEDULER_BOOKING_END_HOUR)
    .minute(0)
    .second(0);

  if (
    startTime.isBefore(windowStart) ||
    !startTime.isBefore(windowEnd) ||
    endTime.isAfter(windowEnd)
  ) {
    return { valid: false, message: SCHEDULER_HOURS_ERROR };
  }
  return { valid: true };
}

export function schedulerBookingMinTime() {
  return dayjs().hour(SCHEDULER_BOOKING_START_HOUR).minute(0).second(0);
}

export function schedulerBookingMaxTime() {
  return dayjs().hour(SCHEDULER_BOOKING_END_HOUR).minute(0).second(0);
}

export const resourceFields = {
  idField: "user_id",
  textField: "user_name",
  subTextField: "availabilityLabel",
  avatarField: "user_name",
  colorField: "color",
};

export const getRandomColor = () => {
  const red = Math.floor(Math.random() * 151) + 50;
  const green = Math.floor(Math.random() * 151) + 50;
  const blue = Math.floor(Math.random() * 151) + 50;
  const color = `rgba(${red}, ${green}, ${blue})`;
  return color;
};

// Scheduler uses JS Dates for positioning. The backend sends "EST wall-clock" times,
// but may label them like UTC (e.g. "...Z"). We intentionally treat incoming times as
// "floating" (wall-clock) and rebuild Dates in the *displayTimezone* so 9:00 always
// lands in the 9:00 slot regardless of the viewer timezone.
export const floatingToDate = (value, displayTimezone) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  let s = String(value);
  // strip milliseconds
  s = s.replace(/\.\d{3}/, "");
  // strip timezone suffix (Z or ±HH:mm / ±HHmm)
  s = s.replace(/Z$/, "").replace(/([+-]\d{2}:?\d{2})$/, "");
  // normalize separator
  s = s.replace(" ", "T");

  return dayjs.tz(s, displayTimezone).toDate();
};

export const groupedSchedules = (data, displayTimezone) => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  const dataMapped = data.map((schedule) => {
      const startDate = floatingToDate(schedule.start, displayTimezone);
      const endDate = floatingToDate(schedule.end, displayTimezone);
      const scheduleStartDate = floatingToDate(
        schedule.scheduleStart,
        displayTimezone,
      );

      const size =
        startDate && endDate ? endDate.getTime() - startDate.getTime() : 0;
      const isZeroDuration = !startDate || !endDate || size <= 0;

      const scheduleStartTimestamp = scheduleStartDate
        ? scheduleStartDate.getTime()
        : null;
      const isNewToSalon = !!schedule.is_new_to_salon;
      const isNewToStylist = !!schedule.is_new_to_stylist;
      const segmentGapOverrides = parseSegmentGapOverrides(
        schedule.segment_gap_overrides
      );

      const allSameEvents = data.filter(
        (x) => x.appointmentId === schedule.appointmentId,
      );

      let uniqueServicesForEvent = {};

      if (allSameEvents.length > 1) {
        uniqueServicesForEvent = allSameEvents.reduce((acc, schedule) => {
          if (acc[schedule.service_id]) {
            return acc;
          } else {
            acc[schedule.service_id] = {
              service_id: schedule.service_id,
              title: schedule.title,
              service_name: schedule.title,
              service_shortcut: schedule.service_shortcut,
              service_color: schedule.service_color,
              price_level: schedule.price_level,
              servicePrice: schedule.servicePrice,
            };
            return acc;
          }
        }, {});
      } else {
        uniqueServicesForEvent = [
          {
            service_id: schedule.service_id,
            title: schedule.title,
            service_name: schedule.title,
            service_shortcut: schedule.service_shortcut,
            service_color: schedule.service_color,
            price_level: schedule.price_level,
            servicePrice: schedule.servicePrice,
          },
        ];
      }

      if (schedule.appointmentId === null) {
        return {
          appointmentId: schedule.appointmentId,
          scheduleStart: scheduleStartDate,
          start: startDate,
          end: endDate,
          isZeroDuration,
          event_id: schedule.event_id,
          title: schedule.client_name,
          user_id: schedule.user_id,
          user_name: schedule.user_name,
          appointment_status: schedule.appointment_status,
          cancellation_reason: schedule.cancellation_reason,
          cancellation_reason_id: schedule.cancellation_reason_id ?? null,
          cancellation_reason_name: schedule.cancellation_reason_name ?? null,
          is_late: schedule.is_late,
          client_confirmed_at: schedule.client_confirmed_at ?? null,
          paymentMethod:
            schedule.paymentMethod === undefined || schedule.paymentMethod === null
              ? null
              : Number(schedule.paymentMethod),
          hasReceipt: schedule.receipt_id != null,
          deposits: parseAppointmentDeposits(schedule.deposits),
          isClientConfirmed:
            !!schedule.client_confirmed_at &&
            schedule.appointment_status !== ScheduleEntryStatus.Canceled,
          confirmation_email_sent_at: schedule.confirmation_email_sent_at ?? null,
          hasConfirmationEmail: !!schedule.confirmation_email_sent_at,
          reminder_email_sent_at: schedule.reminder_email_sent_at ?? null,
          hasReminderEmail: !!schedule.reminder_email_sent_at,
          isNewToSalon,
          isNewToStylist,
          segment_gap_overrides: segmentGapOverrides,
          booking_source: schedule.booking_source,
          booked_by_name: schedule.booked_by_name,
          salon_name: schedule.location_name,
          salon_address: schedule.location_address,
          client: {
            client_id: schedule.client_id,
            client_name: schedule.client_name,
            client_email: schedule.client_email || "",
          },
          location: {
            location_id: schedule.location_id,
            location_name: schedule.location_name,
            organization_id: schedule.organization_id,
          },
          uniqueServices: Object.values(uniqueServicesForEvent),
          services: [
            {
              event_id: schedule.event_id,
              service_id: schedule.service_id,
              title: schedule.title,
              service_shortcut: schedule.service_shortcut,
              service_color: schedule.service_color,
              serviceStart: startDate,
              serviceEnd: endDate,
              topOffset: 0,
              size: size * 100,
            },
          ],
        };
      } else {
        return {
          appointmentId: schedule.appointmentId,
          scheduleStart: scheduleStartDate,
          start: startDate,
          end: endDate,
          isZeroDuration,
          event_id: schedule.event_id,
          title: schedule.client_name,
          user_id: schedule.user_id,
          user_name: schedule.user_name,
          appointment_status: schedule.appointment_status,
          cancellation_reason: schedule.cancellation_reason,
          cancellation_reason_id: schedule.cancellation_reason_id ?? null,
          cancellation_reason_name: schedule.cancellation_reason_name ?? null,
          is_late: schedule.is_late,
          client_confirmed_at: schedule.client_confirmed_at ?? null,
          paymentMethod:
            schedule.paymentMethod === undefined || schedule.paymentMethod === null
              ? null
              : Number(schedule.paymentMethod),
          hasReceipt: schedule.receipt_id != null,
          deposits: parseAppointmentDeposits(schedule.deposits),
          isClientConfirmed:
            !!schedule.client_confirmed_at &&
            schedule.appointment_status !== ScheduleEntryStatus.Canceled,
          confirmation_email_sent_at: schedule.confirmation_email_sent_at ?? null,
          hasConfirmationEmail: !!schedule.confirmation_email_sent_at,
          reminder_email_sent_at: schedule.reminder_email_sent_at ?? null,
          hasReminderEmail: !!schedule.reminder_email_sent_at,
          isNewToSalon,
          isNewToStylist,
          segment_gap_overrides: segmentGapOverrides,
          booking_source: schedule.booking_source,
          booked_by_name: schedule.booked_by_name,
          salon_name: schedule.location_name,
          salon_address: schedule.location_address,
          client: {
            client_id: schedule.client_id,
            client_name: schedule.client_name,
            client_email: schedule.client_email || "",
          },
          location: {
            location_id: schedule.location_id,
            location_name: schedule.location_name,
            organization_id: schedule.organization_id,
          },
          uniqueServices: Object.values(uniqueServicesForEvent),
          services: [
            {
              event_id: schedule.event_id,
              service_id: schedule.service_id,
              title: schedule.title,
              service_shortcut: schedule.service_shortcut,
              service_color: schedule.service_color,
              serviceStart: startDate,
              serviceEnd: endDate,
              topOffset: 0,
              size: size,
            },
          ],
        };
      }
    });

  return dataMapped;
};

export const SchedulerAPI = {
  //get: "gateway/68nkedd2v8", //Uses production/Live API
  // get: `gateway/fh3c0gu345`, //Uses Development API
  get: "scheduler",
  create: "scheduler/createAppointment",
  modify: "scheduler/updateAppointment",
  delete: "scheduler/deleteAppointment",
  move: "scheduler/updateAppointment",
  columnOrder: "scheduler/column-order",
};

/** Wall-clock timezone for scheduler API payloads (keep in sync with backend expectations). */
export const SCHEDULER_TIMEZONE = "America/New_York";

export function formatFloatingWallClock(
  value,
  useTimezone = false,
  format = "h:mm A"
) {
  if (!value) return "";

  if (typeof value === "string") {
    let s = value;
    s = s.replace(/\.\d{3}/, "");
    s = s.replace(/Z$/i, "").replace(/([+-]\d{2}:?\d{2})$/, "");
    s = s.replace(" ", "T");
    if (useTimezone) {
      return dayjs.tz(s, SCHEDULER_TIMEZONE).format(format);
    }
    return dayjs.utc(s).format(format);
  }

  const date = dayjs.isDayjs?.(value) ? value.toDate() : value;
  if (useTimezone) {
    return dayjs(date).tz(SCHEDULER_TIMEZONE).format(format);
  }
  return dayjs(date).format(format);
}

/**
 * Format a calendar Date as salon wall-clock for updateAppointment.
 * Backend stores NY wall-clock with a fake Z — send the digits only, no offset rewrite.
 * Used by drag/move, reschedule, duration, and gap edits.
 */
export function formatScheduleUpdateStart(start) {
  if (!start) return "";
  return dayjs(start).tz(SCHEDULER_TIMEZONE).format("YYYY-MM-DD HH:mm:ss");
}

/** Alias — same wall-clock formatting as formatScheduleUpdateStart. */
export function formatExistingAppointmentStart(start) {
  return formatScheduleUpdateStart(start);
}

/** Full appointment start/end from all segments on the scheduler. */
export function getAppointmentTimeRange(appointment, allSchedules = []) {
  const segments = allSchedules.filter(
    (e) =>
      e.appointmentId === appointment.appointmentId &&
      e.user_id === appointment.user_id
  );
  const list = segments.length > 0 ? segments : [appointment];

  let start = dayjs(list[0].scheduleStart ?? list[0].start);
  let end = dayjs(list[0].end);

  for (const seg of list) {
    const segStart = dayjs(seg.scheduleStart ?? seg.start);
    const segEnd = dayjs(seg.end);
    if (segStart.isBefore(start)) start = segStart;
    if (segEnd.isAfter(end)) end = segEnd;
  }

  return { start, end, durationMinutes: end.diff(start, "minute") };
}

/** Service list payload for updateAppointment (deduped by service_id). */
export function getAppointmentServicesForUpdate(appointment) {
  if (appointment?.uniqueServices?.length) {
    return appointment.uniqueServices.map((s) => ({
      service_id: s.service_id,
    }));
  }
  const byId = new Map();
  for (const s of appointment?.services ?? []) {
    if (s?.service_id != null) {
      byId.set(String(s.service_id), { service_id: s.service_id });
    }
  }
  return [...byId.values()];
}
